import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useJsApiLoader, GoogleMap, Marker } from '@react-google-maps/api';
import {
  CheckCircleIcon,
  ClockIcon,
  CreditCardIcon,
  MagnifyingGlassIcon,
  TruckIcon,
  MapPinIcon,
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
  XCircleIcon,
  ArrowRightIcon,
  StarIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-toastify';
import Header from './app/Header';
import Footer from './app/Footer';
import FooterCopyright from './app/FooterCopyright';
import supabase from './utils/supabaseClient';
import { SERVICES, formatBRL } from './utils/pricing';

const MAPS_LIBRARIES = ['places'];

const STATUS_META = {
  criado:               { label: 'Pedido criado',         step: 1, color: 'text-ink-500'    },
  aguardando_pagamento: { label: 'Aguardando pagamento',  step: 1, color: 'text-accent-600' },
  pago:                 { label: 'Pagamento confirmado',  step: 2, color: 'text-brand-700'  },
  buscando_entregador:  { label: 'Buscando entregador',   step: 2, color: 'text-brand-700'  },
  aceito:               { label: 'Entregador a caminho',  step: 3, color: 'text-brand-700'  },
  em_coleta:            { label: 'Entregador no local',   step: 3, color: 'text-brand-700'  },
  coletado:             { label: 'Encomenda coletada',    step: 4, color: 'text-brand-700'  },
  em_rota:              { label: 'A caminho do destino',  step: 4, color: 'text-brand-700'  },
  entregue:             { label: 'Entregue',              step: 5, color: 'text-green-700'  },
  cancelado:            { label: 'Cancelado',             step: 0, color: 'text-red-600'    },
};

const TIMELINE = [
  { key: 'criado',   label: 'Pedido criado',     Icon: CheckCircleIcon },
  { key: 'pago',     label: 'Pagamento',         Icon: CreditCardIcon },
  { key: 'aceito',   label: 'Entregador',        Icon: MagnifyingGlassIcon },
  { key: 'coletado', label: 'Coletado',          Icon: TruckIcon },
  { key: 'entregue', label: 'Entregue',          Icon: MapPinIcon },
];

const fmtDateTime = (iso) => iso
  ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  : null;

const PedidoStatus = () => {
  const { codigo } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pedido, setPedido]             = useState(undefined); // undefined = loading, null = not found
  const [refreshing, setRefreshing]     = useState(false);
  const [redirecting, setRedirecting]   = useState(false);
  const [entregadorLoc, setEntregadorLoc] = useState(null);
  const [eta, setEta] = useState(null); // { duration_text, duration_min, distance_text }
  const [cancelOpen, setCancelOpen]     = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling]     = useState(false);
  const [ratingStars, setRatingStars]   = useState(0);
  const [ratingHover, setRatingHover]   = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);

  const { isLoaded: mapsLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: MAPS_LIBRARIES,
  });

  const loadPedido = useCallback(async () => {
    setRefreshing(true);
    const { data, error } = await supabase.rpc('get_pedido_publico', { codigo });
    setRefreshing(false);
    if (error) {
      console.error(error);
      setPedido(null);
      return;
    }
    setPedido((data && data.length > 0) ? data[0] : null);
  }, [codigo]);

  const goToCheckout = async () => {
    setRedirecting(true);
    try {
      const r = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ codigo_rastreio: codigo }),
      });
      const data = await r.json();
      if (!r.ok || !data.url) {
        toast.error(data.error || 'Erro ao iniciar pagamento.');
        setRedirecting(false);
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      console.error(e);
      toast.error('Erro de conexão. Tente novamente.');
      setRedirecting(false);
    }
  };

  const submitCancel = async () => {
    setCancelling(true);
    try {
      const r = await fetch('/api/cancelar-pedido', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          codigo_rastreio: codigo,
          motivo: cancelReason.trim() || null,
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.error || 'Erro ao cancelar.');
        return;
      }
      toast.success(data.refund
        ? 'Pedido cancelado. Reembolso iniciado, leva até 7 dias para retornar ao seu cartão/Pix.'
        : 'Pedido cancelado.');
      setCancelOpen(false);
      loadPedido();
    } catch (e) {
      console.error(e);
      toast.error('Erro de conexão.');
    } finally {
      setCancelling(false);
    }
  };

  const submitRating = async () => {
    if (ratingStars < 1) { toast.error('Escolha de 1 a 5 estrelas.'); return; }
    setSubmittingRating(true);
    const { error } = await supabase.rpc('avaliar_pedido', {
      p_codigo: codigo,
      p_estrelas: ratingStars,
      p_comentario: ratingComment.trim() || null,
    });
    setSubmittingRating(false);
    if (error) {
      toast.error(error.message || 'Erro ao avaliar.');
      return;
    }
    toast.success('Obrigado pela avaliação!');
    loadPedido();
  };

  // Feedback ao retornar do Stripe Checkout
  useEffect(() => {
    const paid = searchParams.get('paid');
    if (paid === 'success') {
      toast.success('Pagamento iniciado! Vamos confirmar em instantes.');
      // Remove o param da URL pra não repetir o toast em renders subsequentes
      const next = new URLSearchParams(searchParams);
      next.delete('paid');
      next.delete('sid');
      setSearchParams(next, { replace: true });
    } else if (paid === 'cancel') {
      toast.info('Pagamento cancelado. Você pode tentar novamente.');
      const next = new URLSearchParams(searchParams);
      next.delete('paid');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadPedido(); }, [loadPedido]);

  // Polling da localização do entregador (durante status ativo)
  useEffect(() => {
    if (!pedido?.codigo_rastreio) return;
    const activeStatuses = ['aceito','em_coleta','coletado','em_rota'];
    if (!activeStatuses.includes(pedido.status)) {
      setEntregadorLoc(null);
      return;
    }

    let cancelled = false;
    const fetchLoc = async () => {
      const { data, error } = await supabase.rpc('get_entregador_loc_publico', { codigo: pedido.codigo_rastreio });
      if (cancelled) return;
      if (!error && data && data.length > 0) {
        setEntregadorLoc({
          lat: Number(data[0].lat),
          lng: Number(data[0].lng),
          updated_at: data[0].updated_at,
        });
      }
    };
    fetchLoc();
    const t = setInterval(fetchLoc, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, [pedido?.codigo_rastreio, pedido?.status]);

  // ETA: calcula rota da posição do entregador até o destino apropriado
  // (coleta enquanto status=aceito|em_coleta; entrega depois disso)
  useEffect(() => {
    if (!mapsLoaded || !entregadorLoc || !pedido) { setEta(null); return; }
    const goingToPickup = ['aceito','em_coleta'].includes(pedido.status);
    const goingToDropoff = ['coletado','em_rota'].includes(pedido.status);
    if (!goingToPickup && !goingToDropoff) { setEta(null); return; }

    const destination = goingToPickup ? pedido.endereco_coleta : pedido.endereco_entrega;
    if (!destination) return;

    const ds = new window.google.maps.DirectionsService();
    let cancelled = false;
    ds.route({
      origin: { lat: entregadorLoc.lat, lng: entregadorLoc.lng },
      destination,
      travelMode: window.google.maps.TravelMode.DRIVING,
    }, (result, status) => {
      if (cancelled) return;
      if (status !== 'OK' || !result?.routes?.[0]?.legs?.[0]) {
        setEta(null);
        return;
      }
      const leg = result.routes[0].legs[0];
      setEta({
        duration_text:  leg.duration.text,
        duration_min:   Math.round(leg.duration.value / 60),
        distance_text:  leg.distance.text,
        going_to:       goingToPickup ? 'coleta' : 'entrega',
      });
    });
    return () => { cancelled = true; };
  }, [mapsLoaded, entregadorLoc, pedido]);

  // Realtime: assina mudanças no pedido
  useEffect(() => {
    if (!pedido?.codigo_rastreio) return;
    if (pedido.status === 'entregue' || pedido.status === 'cancelado') return;

    const channel = supabase
      .channel(`pedido-${pedido.codigo_rastreio}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'pedidos',
          filter: `codigo_rastreio=eq.${pedido.codigo_rastreio}`,
        },
        () => loadPedido()
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [pedido?.codigo_rastreio, pedido?.status, loadPedido]);

  if (pedido === undefined) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center py-40">
          <div className="h-10 w-10 rounded-full border-4 border-brand-200 border-t-brand-600 animate-spin" />
        </div>
        <Footer />
        <FooterCopyright />
      </div>
    );
  }

  if (pedido === null) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <section className="container mx-auto px-4 lg:px-6 py-20 max-w-md">
          <div className="card p-8 text-center">
            <XCircleIcon className="h-12 w-12 text-ink-300 mx-auto" />
            <h1 className="mt-4 font-display text-xl font-bold text-ink-900">Pedido não encontrado</h1>
            <p className="mt-2 text-sm text-ink-500">
              O código <strong className="text-ink-900">{codigo}</strong> não existe ou foi cancelado.
            </p>
            <Link to="/" className="mt-6 btn-primary inline-flex">Voltar para a home</Link>
          </div>
        </section>
        <Footer />
        <FooterCopyright />
      </div>
    );
  }

  const meta = STATUS_META[pedido.status] ?? STATUS_META.criado;
  const StatusIcon = (
    pedido.status === 'entregue'  ? CheckCircleIcon :
    pedido.status === 'cancelado' ? XCircleIcon :
    pedido.status === 'em_rota' || pedido.status === 'coletado' ? TruckIcon :
    ClockIcon
  );
  const service = SERVICES[pedido.servico_id] ?? SERVICES.frete_pequeno;

  // Whatsapp link com mensagem pré-preenchida
  const wppMsg = encodeURIComponent(`Olá! Sou o cliente do pedido ${pedido.codigo_rastreio} (${pedido.cliente_nome}).`);
  const wppLink = pedido.entregador_whatsapp
    ? `https://wa.me/55${(pedido.entregador_whatsapp || '').replace(/\D/g, '')}?text=${wppMsg}`
    : null;

  const isPagamentoPendente = pedido.pagamento_status !== 'aprovado'
    && pedido.status === 'aguardando_pagamento';

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 bg-mesh-light" aria-hidden="true" />
        <div className="relative container mx-auto px-4 lg:px-6 pt-12 pb-6 max-w-2xl">
          <div className="flex items-start justify-between gap-3">
            <div>
              <span className="badge-brand mb-3">Pedido</span>
              <h1 className="font-display text-display-lg text-ink-900 break-all">
                {pedido.codigo_rastreio}
              </h1>
              <p className="mt-2 text-ink-500">
                {service.name} · {Number(pedido.distancia_km).toFixed(1)} km · {formatBRL(pedido.preco)}
              </p>
            </div>
            <button
              onClick={loadPedido}
              className="btn-ghost text-sm gap-1.5 mt-1"
              title="Atualizar"
            >
              <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Atualizar</span>
            </button>
          </div>
        </div>
      </section>

      <section className="pb-16 lg:pb-24">
        <div className="container mx-auto px-4 lg:px-6 max-w-2xl space-y-6">

          {/* Status atual */}
          <div className="card p-6">
            <div className="flex items-center gap-4">
              <div className={`h-14 w-14 rounded-2xl flex items-center justify-center shrink-0 bg-brand-50 ${meta.color}`}>
                <StatusIcon className="h-7 w-7" />
              </div>
              <div>
                <p className="text-sm text-ink-500 font-medium">Status</p>
                <p className={`text-xl font-display font-bold ${meta.color}`}>{meta.label}</p>
              </div>
            </div>

            {/* Timeline */}
            {pedido.status !== 'cancelado' && (
              <ol className="flex items-center mt-6">
                {TIMELINE.map(({ key, label, Icon }, idx) => {
                  const stepNum = idx + 1;
                  const done   = meta.step > stepNum;
                  const active = meta.step === stepNum;
                  return (
                    <li key={key} className="flex items-center flex-1 last:flex-none">
                      <div className="flex flex-col items-center gap-1">
                        <div
                          className={`h-9 w-9 rounded-xl flex items-center justify-center transition
                            ${done   ? 'bg-brand-gradient text-white shadow-glow' : ''}
                            ${active ? 'bg-brand-gradient text-white shadow-glow ring-4 ring-brand-100 animate-pulse' : ''}
                            ${!done && !active ? 'bg-ink-100 text-ink-400' : ''}`}
                        >
                          {done ? <CheckCircleIcon className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                        </div>
                        <span className={`text-[11px] font-medium ${active ? 'text-ink-900' : 'text-ink-400'}`}>
                          {label}
                        </span>
                      </div>
                      {idx < TIMELINE.length - 1 && (
                        <div className={`flex-1 h-0.5 mx-1.5 mb-5 ${done ? 'bg-brand-500' : 'bg-ink-100'}`} />
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
          </div>

          {/* CTA pagamento */}
          {isPagamentoPendente && (
            <div className="card p-6 bg-accent-50/40 border-accent-200">
              <div className="flex items-start gap-3">
                <CreditCardIcon className="h-6 w-6 text-accent-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-display font-bold text-ink-900">Pague para liberar a coleta</p>
                  <p className="text-sm text-ink-600 mt-1">
                    Total: <strong className="text-ink-900">{formatBRL(pedido.preco)}</strong>.
                    Você pode pagar com Pix ou cartão. O entregador é acionado
                    assim que o pagamento for confirmado.
                  </p>

                  <button
                    type="button"
                    onClick={goToCheckout}
                    disabled={redirecting}
                    className="mt-4 btn-accent w-full"
                  >
                    {redirecting ? 'Redirecionando…' : (
                      <>Pagar agora <ArrowRightIcon className="h-4 w-4" /></>
                    )}
                  </button>
                  <p className="mt-3 text-xs text-ink-500 text-center">
                    Pagamento processado por Stripe · ambiente seguro
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Cancelamento (antes do entregador aceitar) */}
          {['aguardando_pagamento','pago','buscando_entregador'].includes(pedido.status) && (
            <div className="text-center">
              <button
                type="button"
                onClick={() => setCancelOpen(true)}
                className="text-xs text-ink-500 hover:text-red-600 underline"
              >
                Cancelar este pedido
              </button>
            </div>
          )}

          {/* Mapa em tempo real (quando há localização do entregador) */}
          {entregadorLoc && mapsLoaded && (
            <div className="card overflow-hidden">
              <GoogleMap
                center={{ lat: entregadorLoc.lat, lng: entregadorLoc.lng }}
                zoom={15}
                mapContainerStyle={{ width: '100%', height: '280px' }}
                options={{
                  disableDefaultUI: true,
                  zoomControl: true,
                  styles: [
                    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
                    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
                  ],
                }}
              >
                <Marker
                  position={{ lat: entregadorLoc.lat, lng: entregadorLoc.lng }}
                  title="Entregador"
                />
              </GoogleMap>
              {eta && (
                <div className="px-4 py-3 bg-brand-gradient text-white">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-[11px] uppercase tracking-wider opacity-80 font-bold">
                        Chegando à {eta.going_to}
                      </p>
                      <p className="font-display font-bold text-lg">
                        ~ {eta.duration_text}
                      </p>
                    </div>
                    <p className="text-xs opacity-80">{eta.distance_text}</p>
                  </div>
                </div>
              )}
              <div className="px-4 py-2.5 text-xs text-ink-500 flex items-center gap-1.5 bg-ink-50 border-t border-ink-100">
                <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                Posição ao vivo · atualizada {fmtDateTime(entregadorLoc.updated_at)}
              </div>
            </div>
          )}

          {/* Entregador (quando atribuído) */}
          {pedido.entregador_nome && (
            <div className="card p-6">
              <p className="text-xs font-bold text-ink-400 uppercase tracking-wider mb-3">Entregador</p>
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-2xl bg-brand-gradient text-white flex items-center justify-center font-display font-bold shadow-glow">
                  {(pedido.entregador_nome || '?').charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-ink-900 truncate">{pedido.entregador_nome}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {pedido.entregador_avaliacao_media != null && (
                      <span className="inline-flex items-center gap-0.5 text-xs text-ink-500">
                        <StarSolidIcon className="h-3 w-3 text-accent-500" />
                        {Number(pedido.entregador_avaliacao_media).toFixed(1)}
                      </span>
                    )}
                    {pedido.entregador_whatsapp && (
                      <span className="text-xs text-ink-500 truncate">· {pedido.entregador_whatsapp}</span>
                    )}
                  </div>
                </div>
                {wppLink && (
                  <a
                    href={wppLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary text-sm gap-1.5 shrink-0"
                  >
                    <ChatBubbleLeftRightIcon className="h-4 w-4" />
                    WhatsApp
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Comprovante de entrega (foto) */}
          {pedido.status === 'entregue' && pedido.comprovante_url && (
            <div className="card p-6">
              <p className="text-xs font-bold text-ink-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <PhotoIcon className="h-3.5 w-3.5" /> Comprovante de entrega
              </p>
              <a href={pedido.comprovante_url} target="_blank" rel="noopener noreferrer" className="block">
                <img
                  src={pedido.comprovante_url}
                  alt="Comprovante de entrega"
                  className="w-full max-h-80 object-contain rounded-2xl bg-ink-50 cursor-zoom-in"
                />
              </a>
              <p className="text-xs text-ink-400 text-center mt-3">
                Foto enviada pelo entregador no momento da entrega.
              </p>
            </div>
          )}

          {/* Avaliação pós-entrega */}
          {pedido.status === 'entregue' && (
            <div className="card p-6">
              {pedido.avaliacao_estrelas ? (
                <>
                  <p className="text-xs font-bold text-ink-400 uppercase tracking-wider mb-3">Sua avaliação</p>
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map((n) => (
                      <StarSolidIcon
                        key={n}
                        className={`h-6 w-6 ${n <= pedido.avaliacao_estrelas ? 'text-accent-500' : 'text-ink-200'}`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-ink-500 mt-2">
                    Avaliada em {fmtDateTime(pedido.avaliacao_em)}
                  </p>
                </>
              ) : (
                <>
                  <p className="font-display font-bold text-ink-900 mb-1">Como foi a entrega?</p>
                  <p className="text-sm text-ink-500 mb-4">
                    Sua opinião nos ajuda a melhorar o serviço.
                  </p>
                  <div className="flex items-center gap-1 mb-4">
                    {[1,2,3,4,5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onMouseEnter={() => setRatingHover(n)}
                        onMouseLeave={() => setRatingHover(0)}
                        onClick={() => setRatingStars(n)}
                        className="p-0.5"
                        aria-label={`${n} estrelas`}
                      >
                        {n <= (ratingHover || ratingStars) ? (
                          <StarSolidIcon className="h-9 w-9 text-accent-500" />
                        ) : (
                          <StarIcon className="h-9 w-9 text-ink-300 hover:text-accent-300" />
                        )}
                      </button>
                    ))}
                  </div>
                  <textarea
                    className="input min-h-[80px] resize-y mb-3"
                    value={ratingComment}
                    onChange={(e) => setRatingComment(e.target.value)}
                    placeholder="Conte mais sobre a entrega (opcional)…"
                    maxLength={1000}
                  />
                  <button
                    onClick={submitRating}
                    disabled={submittingRating || ratingStars < 1}
                    className="btn-primary w-full gap-2"
                  >
                    {submittingRating ? 'Enviando…' : (<><CheckCircleIcon className="h-4 w-4" /> Enviar avaliação</>)}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Endereços */}
          <div className="card p-6">
            <p className="text-xs font-bold text-ink-400 uppercase tracking-wider mb-3">Trajeto</p>
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className="h-3 w-3 rounded-full bg-brand-600 ring-4 ring-brand-100" />
                  <div className="flex-1 w-px bg-ink-200 my-1" />
                </div>
                <div className="pb-3">
                  <p className="text-xs font-bold text-ink-400 uppercase tracking-wider">Coleta</p>
                  <p className="text-sm text-ink-900 font-medium mt-0.5">{pedido.endereco_coleta}</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex flex-col items-center">
                  <MapPinIcon className="h-4 w-4 text-accent-600 -mx-0.5" />
                </div>
                <div>
                  <p className="text-xs font-bold text-ink-400 uppercase tracking-wider">Entrega</p>
                  <p className="text-sm text-ink-900 font-medium mt-0.5">{pedido.endereco_entrega}</p>
                  {pedido.descricao_item && (
                    <p className="text-xs text-ink-500 mt-1">📦 {pedido.descricao_item}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Histórico timestamps */}
          <div className="card p-6">
            <p className="text-xs font-bold text-ink-400 uppercase tracking-wider mb-3">Histórico</p>
            <ul className="space-y-2 text-sm">
              {[
                ['Pedido criado',          pedido.created_at],
                ['Pagamento confirmado',   pedido.pago_at],
                ['Entregador a caminho',   pedido.aceito_at],
                ['Encomenda coletada',     pedido.coletado_at],
                ['Entregue',               pedido.entregue_at],
              ].filter(([, t]) => t).map(([label, t]) => (
                <li key={label} className="flex justify-between gap-2 border-b border-ink-50 pb-1.5 last:border-0">
                  <span className="text-ink-500">{label}</span>
                  <span className="text-ink-900 font-medium">{fmtDateTime(t)}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Rodapé com link salvar */}
          <p className="text-center text-xs text-ink-400">
            Salve esta página para acompanhar. Link único do seu pedido:
            <br />
            <code className="font-mono text-brand-700">/pedido/{pedido.codigo_rastreio}</code>
          </p>

        </div>
      </section>

      {/* Modal cancelamento */}
      {cancelOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ink-900/60 backdrop-blur-sm"
             onClick={() => !cancelling && setCancelOpen(false)}>
          <div onClick={(e) => e.stopPropagation()}
               className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl shadow-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-ink-100">
              <p className="font-display font-bold text-ink-900">Cancelar pedido</p>
              <p className="text-xs text-ink-500 mt-1">{pedido.codigo_rastreio}</p>
            </div>
            <div className="px-6 py-5 space-y-3 overflow-y-auto">
              <p className="text-sm text-ink-700 leading-relaxed">
                {pedido.pagamento_status === 'aprovado'
                  ? 'O valor pago será reembolsado automaticamente para o mesmo meio de pagamento. Pode levar até 7 dias úteis.'
                  : 'Como o pagamento ainda não foi processado, nada será cobrado.'}
              </p>
              <div>
                <label className="label" htmlFor="cancel-motivo">Motivo (opcional)</label>
                <textarea
                  id="cancel-motivo"
                  className="input min-h-[80px] resize-y"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Mudei de ideia, vou levar pessoalmente, etc."
                  maxLength={1000}
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-ink-100 flex gap-2">
              <button onClick={() => setCancelOpen(false)} disabled={cancelling}
                      className="btn-ghost text-sm flex-1">
                Voltar
              </button>
              <button onClick={submitCancel} disabled={cancelling}
                      className="bg-red-600 hover:bg-red-700 disabled:bg-ink-200 disabled:text-ink-400 text-white font-semibold px-4 py-2.5 rounded-xl text-sm transition flex-1">
                {cancelling ? 'Cancelando…' : 'Cancelar pedido'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
      <FooterCopyright />
    </div>
  );
};

export default PedidoStatus;
